import { addItemToCart } from '@/actions/addItemToCart';
import { PaymentContext } from '@/context/paymentContext';
import { ProductContext } from '@/context/productContext';
import { WebEngageContext } from '@/context/webengageContext';
import ImageWithFallback from '@/globalComponents/image';
import { AppScreens, WebEngageEventData, WebEngageEventNames } from '@/types/wegengage';
import { LightBenefitIconMap } from '@/utils';
import ProductUtils from '@/utils/productUtils';
import WebEngageUtils from '@/utils/webengageUtils';
import { useRouter } from 'next/router';
import React, { useContext, useMemo, useState } from 'react';
import { PlanCardProps } from '.';
import FallBackImage from '../../../public/images/broken-image.svg';
import { getFormattedPrice } from '../MultiYearPlanPricing/utils';
import { PlanCardBanner } from './PlanCardBanner';
import styles from './styles.module.scss';
import { ErrorModal } from './errorModal';
import Image from 'next/image';
import RecommendedTagWeb from '../../../public/images/recommended-tag-web.svg';
import { Button } from '@/globalComponents/button';
import Loader from '@/globalComponents/loader';

export const calculateDuration = ({ duration, unit }: { duration: number; unit: string }) => {
  if (unit.toLowerCase() === 'y') {
    return duration === 1 ? 'Year' : `${duration} Years`;
  }
  return duration === 1 ? 'Month' : `${duration} Months`;
};

interface PlanCardAdditionalProps {
  disableChoosePlan: boolean;
  setDisableChoosePlan: Function;
}

export const PlanCard = (props: PlanCardProps & PlanCardAdditionalProps) => {
  const {
    id,
    benefits,
    validity,
    price,
    tier,
    recommended,
    show: isAllTrue,
    disableChoosePlan,
    setDisableChoosePlan,
  } = props;

  const router = useRouter();
  const { state: webengageState } = useContext(WebEngageContext);

  const { state: productState } = useContext(ProductContext);

  const { dispatch: productAction } = useContext(ProductContext);

  const { dispatch: paymentAction } = useContext(PaymentContext);

  const lowerCaseTier = tier.toLocaleLowerCase();

  const [loading, setLoading] = useState(false);

  const [isAddItemTocartError, setIsAddItemToCartError] = useState(false);

  const onChoosePlanButtonClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setDisableChoosePlan(true);
    setLoading(true);
    paymentAction.setT_CStatus(false);
    const _we = window.webengage;
    if (_we) {
      const productUtils = new ProductUtils(productState.selectedProduct);
      const eventData: WebEngageEventData = {
        ...webengageState.webengageDefault,
        navigated_from: AppScreens.AMC_TIER_SELECTION_SCREEN,
        customer_type: productState.selectedProduct?.expiryStatus?.status,
        product_flag: productUtils.getProductLabel(),
        product_selected: productState.selectedProduct.name,
        tiers_visible: WebEngageUtils.getTiersVisible(productState.amcPlans?.webData),
        amc_tier: tier,
        recommended_flag: recommended,
        customer_code: WebEngageUtils.stringUtil(productState.selectedProduct?.customerCode),
      };
      _we.track(WebEngageEventNames.AMC_TIER_SELECTED, eventData);
    }
    e.preventDefault();
    productAction.setSelectedTier(lowerCaseTier);
    productAction.setSelectedMultiYearPlan(null);

    try {
      await addItemToCart(
        productState.selectedProduct.id, //assetId
        id, //product_variant_id
      );
      router
        .push({
          pathname: `/cart`,
          query: {
            assetid: productState.selectedProduct.id,
          },
        })
        .then(() => {
          setDisableChoosePlan(false);
          setLoading(false);
        });
    } catch (error) {
      setDisableChoosePlan(false);
      setIsAddItemToCartError(true);
      setLoading(false);
    }
  };

  const shouldShowPrice = useMemo(() => {
    return price !== 0 && price !== null && price !== undefined;
  }, [price]);

  const shouldShowBenefit = (benefitText: string | undefined) => {
    return benefitText?.length !== 0 && benefitText !== null && benefitText !== undefined;
  };
  return (
    <div
      data-testid={`amc-plan-${tier}-card`}
      className={`${styles[lowerCaseTier]} ${styles.card} w-full`}
    >
      <div className="flex flex-col">
        <PlanCardBanner tier={lowerCaseTier} isAllTrue={isAllTrue} />
        <div
          data-testid="amc-plan-benefit"
          className="flex flex-col gap-[0.8125rem] md:gap-[1.125rem] px-[0.8125rem] mt-4"
        >
          {benefits.map((benefit) => {
            if (shouldShowBenefit(benefit.desc) === false) {
              return null;
            }
            const benefitIcon = LightBenefitIconMap[benefit.icon];
            return (
              <div
                key={benefit.id}
                className={`flex ${isAllTrue ? 'gap-[0.375rem]' : 'gap-s4'} items-center`}
              >
                {benefitIcon ? (
                  <ImageWithFallback
                    data-testid="amc-plan-benefit-icon"
                    src={benefitIcon}
                    alt={benefit.icon}
                    height={24}
                    width={24}
                    className="w-6 h-6"
                  />
                ) : (
                  <ImageWithFallback
                    data-testid="amc-plan-benefit-icon"
                    src={FallBackImage}
                    alt="icon not available"
                    height={27}
                    width={20}
                  />
                )}
                <p
                  data-testid="amc-plan-description"
                  className={`font-medium !leading-[1.125rem] text-[0.875rem]`}
                >
                  {benefit.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
      <div className="px-4 flex flex-col">
        {shouldShowPrice && (
          <p
            data-testid="amc-plan-price"
            className={`${isAllTrue ? 'mt-14' : 'mt-[3.5625rem]'}  font-bold text-2xl leading-9`}
          >
            {getFormattedPrice(price)}
            <span
              data-testid="amc-plan-duration"
              className={styles.duration}
            >{`/ ${calculateDuration(validity)}`}</span>
          </p>
        )}
        <Button
          disabled={loading || disableChoosePlan}
          dataTestId={`select-${lowerCaseTier}-plan`}
          classNames={`${styles.selectPlan} ${styles[lowerCaseTier]} grid place-content-center ${
            isAllTrue ? 'mt-6 max-h-10' : 'mt-[1.875rem] max-h-8'
          }`}
          variant={'primary'}
          onClick={onChoosePlanButtonClick}
        >
          <>
            {loading ? (
              <Loader variant={lowerCaseTier === 'platinum' ? 'secondary' : 'primary'} />
            ) : (
              <p className="uppercase text-[0.875rem] leading-[18px]">Select Plan</p>
            )}
          </>
        </Button>
      </div>
      {recommended && (
        <div
          data-testid="amc-plan-recomended-tag"
          className="absolute -top-3 -left-1 max-w-[114px]"
        >
          <Image
            data-testid="recommended-tag"
            height={32}
            width={114}
            src={RecommendedTagWeb}
            alt={'recommended'}
          />
        </div>
      )}
      {isAddItemTocartError && (
        <ErrorModal isError={isAddItemTocartError} setIsError={setIsAddItemToCartError} />
      )}
    </div>
  );
};
